import { format } from 'date-fns'
import * as React from 'react'

import { usePagination } from 'hooks/usePagination'
import { useStores } from 'hooks/useStores'
import {
  useGetBranchesQuery,
  useGetRegionsQuery,
  useGetStatusesQuery,
} from 'redux/services/common'
import { ApplicationApi, ApplicationParams } from 'services/api/ApplicaitionApi'

import { AppLayoutTable } from 'components/AppLayoutTable'
import { AppLoader } from 'components/AppLoader'
import { AppPagination } from 'components/AppPagination'
import { AppStatistics } from 'components/AppStatistics/AppStatistics'
import { AppTable } from 'components/AppTable'

import { Application } from 'models/application'
import { IStatistic } from 'models/statistic'
import { HistoryStatusObject } from 'models/status'
import { UserRole } from 'models/user'

import { formatDate } from 'shared/libs'

import { ApplicationDetails } from './components/ApplicationDetails'
import { ApplicationDetailsEmpty } from './components/ApplicationDetailsEmpty'
import { ApplicationDetailsHeader } from './components/ApplicationDetailsHeader'
import { ApplicationImageModal } from './components/ApplicationImageModal'

export const ApplicationsPage = () => {
  const { userStore } = useStores()
  const { dialogStore } = useStores()
  const { data: regions = [] } = useGetRegionsQuery()
  const { data: statuses = [] } = useGetStatusesQuery()
  const { data: branches = [] } = useGetBranchesQuery()

  const [disabled, setDisabled] = React.useState(false)
  const [details, setDetails] = React.useState<Application | null>(null)
  const [statusHistory, setStatusHistory] = React.useState<
    HistoryStatusObject[] | null
  >(null)

  const [lightbox, setLightbox] = React.useState<string[]>([])
  const [attachments, setAttachments] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState(false)
  const [downloadingExel, setDownloadingExel] = React.useState(false)
  const [downloadingExelBonus, setDownloadingExelBonus] = React.useState(false)
  const [downloadingExelForMonth, setDownloadingExelForMonth] =
    React.useState(false)
  const { page, list, count, fetchingList, getList, setParams } = usePagination(
    [UserRole.Administrator, UserRole.Supervisor].includes(
      userStore?.user?.role!,
    )
      ? ApplicationApi.getApplications
      : ApplicationApi.getApplicationsByReports,
  )
  const [statistics, setStatistics] = React.useState<IStatistic[]>([])

  const applyFilters = (filters: ApplicationParams) => {
    const paramsWithJunior = {
      ...filters,
      p_islamic: filters.p_islamic || undefined,
      el_kart: filters.el_kart || undefined,
    }
    setParams(paramsWithJunior)
    getList(1, paramsWithJunior)
    fetchStatistics(paramsWithJunior)
    closeDetails()
  }

  async function fetchStatistics(params: ApplicationParams) {
    const response = await ApplicationApi.getApplicationsStatistics({
      ...params,
      p_islamic: params.p_islamic || undefined,
      el_kart: params.el_kart || undefined,
    })
    if (response.status === 200 && response.data) {
      setStatistics(response.data)
    }
  }

  const downloadApplications = async (filters: ApplicationParams) => {
    setDownloadingExel(true)

    const paramsDownload = {
      ...filters,
      p_islamic: filters.p_islamic || undefined,
      el_kart: filters.el_kart || undefined,
    }
    // const paramsDownload = {...filters, m_junior: filters.m_junior ?? false, p_islamic: filters.p_islamic ? filters.p_islamic : undefined}
    try {
      await ApplicationApi.generateExcel(paramsDownload)

      dialogStore.openModal({
        title: 'Успех',
        description:
          'Ваш файл генерируется. Для того, чтобы отследить процесс или скачать файл, перейдите на вкладку "Ручные отчеты / Карты"',
      })
    } catch {
    } finally {
      setTimeout(() => setDownloadingExel(false), 10000)
    }
  }

  const downloadApplicationsBonus = async (filters: ApplicationParams) => {
    setDownloadingExelBonus(true)
    try {
      const { data } = await ApplicationApi.downloadApplicationsBonus(filters)
      // The actual download
      const filename = `reports_for_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (error) {}
    setDownloadingExelBonus(false)
  }

  const downloadApplicationsForMonth = async (filters: ApplicationParams) => {
    setDownloadingExelForMonth(true)
    try {
      const { data } =
        await ApplicationApi.downloadApplicationsForMonth(filters)
      // The actual download
      const filename = `reports_for_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (error) {}
    setDownloadingExelBonus(false)
  }

  const openDetails = async (application: Application) => {
    try {
      if (application.id === details?.id) {
        return
      }
      closeDetails()
      setLoading(true)

      const [{ data }, { data: statusHistoryByID }] = await Promise.all([
        ApplicationApi.getApplicationById(application.id),
        ApplicationApi.getApplicationStatusHistoryById(application.id),
      ])

      setDetails(data)
      setStatusHistory(
        statusHistoryByID.map(
          ({ changed_at, changed_by, new_status_display, ...rest }) => ({
            created_at: changed_at,
            user: changed_by,
            status: new_status_display,
            ...rest,
          }),
        ),
      )

      const images = []

      if (data?.client_photo) {
        images.push(data.client_photo)
      }

      if (data?.anketa_photo) {
        images.push(data.anketa_photo)
      }

      if (data?.passport_back_photo) {
        images.push(data.passport_back_photo)
      }
      if (data?.passport_front_photo) {
        images.push(data.passport_front_photo)
      }
      setAttachments(images)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }
  const onAccept = async () => {
    try {
      setDisabled(true)
      await ApplicationApi.approveApplication(details?.id as number)

      getList()
      closeDetails()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const onRefuse = async () => {
    try {
      setDisabled(true)
      await ApplicationApi.refuseApplication(details?.id as number)

      getList()
      closeDetails()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const onRework = async (controller_comment: string) => {
    try {
      setDisabled(true)
      await ApplicationApi.reworkApplication(details?.id as number, {
        controller_comment,
      })

      getList()
      closeDetails()
    } catch (error) {
    } finally {
      setDisabled(false)
    }
  }

  const openLightboxAttachments = (index: number) => {
    openLightbox(attachments)
  }

  const openLightbox = (image: string[]) => {
    document.body.style.overflow = 'hidden'

    setLightbox(image)
  }

  const closeLightbox = () => {
    document.body.style.overflow = 'auto'
    setLightbox([])
  }

  const closeDetails = () => {
    setDetails(null)
    setAttachments([])
  }

  React.useEffect(() => {
    fetchStatistics({} as ApplicationParams)
  }, [])

  return (
    <>
      <AppLayoutTable
        header={
          <ApplicationDetailsHeader
            onDownloadExcel={downloadApplications}
            onDownloadExcelBonus={downloadApplicationsBonus}
            downloadApplicationsForMonth={downloadApplicationsForMonth}
            downloadingExel={downloadingExel}
            downloadingExelBonus={downloadingExelBonus}
            downloadingExelForMonth={downloadingExelForMonth}
            onSubmit={applyFilters}
            branches={branches}
            regions={regions}
            statuses={statuses}
          />
        }
        sidebarOpened={Boolean(details)}
        sidebar={
          details === null ? (
            <ApplicationDetailsEmpty loading={loading} />
          ) : (
            <ApplicationDetails
              disabled={disabled}
              attachments={attachments}
              onAccept={onAccept}
              openLivePhoto={() => openLightbox([details.client_photo])}
              openAttachments={openLightboxAttachments}
              setLightbox={setLightbox}
              onRework={onRework}
              onRefuse={onRefuse}
              onClose={closeDetails}
              application={details}
              statusHistory={statusHistory}
            />
          )
        }
        statistics={
          statistics?.length > 0 ? <AppStatistics list={statistics} /> : null
        }
        title="Заявки"
      >
        {fetchingList ? (
          <AppLoader />
        ) : (
          <AppTable
            selected={{
              value: details?.id,
              key: 'id',
            }}
            onClickRow={openDetails}
            headers={[
              {
                data: 'ID',
                key: 'id',
                width: 50,
              },
              {
                data: 'Консультант',
                key: 'consultant',
                render: (_, data) => {
                  return `${data.consultant?.first_name || ''} ${
                    data.consultant?.last_name || ''
                  } ${data.consultant?.middle_name || ''}`
                },
              },
              {
                data: 'Ф.И.О',
                key: 'name',
                render: (_, data) => {
                  return `${data.name} ${data.surname} ${
                    data.middle_name || ''
                  }`
                },
              },
              {
                data: 'Дата подачи',
                key: 'created_at',
                width: 100,
                render: (_, item) => formatDate(item.created_at),
              },
              {
                data: 'Статус',
                key: 'status',
                render: status => {
                  return statuses.find(item => item.code === status)?.name
                },
                width: 100,
              },
              {
                data: 'Регион',
                key: 'region',
                render: (_, data) => {
                  return data.region?.name
                },
                width: 80,
              },
              {
                data: 'ИНН',
                key: 'inn',
                render: (_, data) => {
                  return data.inn || ''
                },
                width: 70,
              },
              {
                data: 'ПАН',
                key: 'pan',
                render: (_, data) => {
                  return data.card?.number || ''
                },
                width: 70,
              },
              {
                data: 'Комментарий',
                key: 'controller_comment',
                render: (_, data) => {
                  let comment = data?.controller_comment || ''
                  return comment
                },
                width: 70,
              },
            ]}
            dataSource={list}
          />
        )}
      </AppLayoutTable>
      {count > 0 ? (
        <AppPagination
          forcePage={page - 1}
          onPageChange={getList}
          count={count}
        />
      ) : null}
      {lightbox.length ? (
        <ApplicationImageModal onClose={closeLightbox} images={lightbox} />
      ) : null}
    </>
  )
}
